import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/brand";
import { useCart } from "@/context/CartContext";
import Crown from "@/components/Crown";

export default function ProductModal({ product, onClose }) {
  const [size, setSize] = useState(null);
  const [view, setView] = useState("front");
  const [email, setEmail] = useState("");
  const [locking, setLocking] = useState(false);
  const [accessReserved, setAccessReserved] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    setSize(product?.sizes?.length === 1 ? product.sizes[0] : null);
    setView("front");
    setEmail("");
    setAccessReserved(false);
  }, [product]);

  const hasViews = Boolean(product?.views);
  const imgSrc = hasViews ? product.views[view] : product?.image;

  const handleAdd = () => {
    if (!size) {
      toast.error("SELECT A SIZE FIRST");
      return;
    }
    addItem(product, size);
    toast.success(`${product.name.toUpperCase()} — SIZE ${size} ADDED`);
    onClose();
  };

  const lockIn = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("ENTER A VALID EMAIL");
      return;
    }
    setLocking(true);
    try {
      await axios.post(`${API}/newsletter`, { email, lookup_key: product.lookup_key });
      setAccessReserved(true);
      toast.success("ACCESS RESERVED");
    } catch (err) {
      toast.error(err.response?.data?.detail || "SOMETHING BROKE — TRY AGAIN");
    } finally {
      setLocking(false);
    }
  };

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          data-testid="product-detail-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-[#0A0A0B]/85 backdrop-blur-md p-0 sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="grid w-full max-w-4xl grid-cols-1 md:grid-cols-2 border border-zinc-700/70 bg-[#111113] max-h-[92vh] overflow-y-auto"
          >
            <div className="relative aspect-[4/5] md:aspect-auto md:min-h-[520px] overflow-hidden bg-black flex items-center">
              <AnimatePresence mode="wait">
                <motion.img
                  key={imgSrc}
                  src={imgSrc}
                  alt={product.name}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  className={`absolute inset-0 h-full w-full ${hasViews ? "object-contain" : "object-cover"}`}
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/60 to-transparent pointer-events-none" />
              <span className="absolute top-4 left-4 bg-zinc-950/80 border border-zinc-700/60 px-3 py-1 text-[9px] tracking-[0.3em] text-zinc-400 uppercase">
                {product.tag}
              </span>
              {hasViews && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex border border-zinc-700/70 bg-zinc-950/85 backdrop-blur" data-testid="product-view-toggle">
                  {["front", "back"].map((v) => (
                    <button
                      key={v}
                      data-testid={`product-view-${v}`}
                      onClick={() => setView(v)}
                      className={`px-5 py-2 text-[10px] tracking-[0.3em] uppercase transition-colors duration-300 ${
                        view === v ? "bg-zinc-100 text-zinc-950 font-bold" : "text-zinc-400 hover:text-zinc-100"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative flex flex-col p-6 sm:p-10">
              <button
                data-testid="product-modal-close"
                onClick={onClose}
                className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              <Crown className="h-5 w-8 text-zinc-600 mb-6" />
              <h3 className="font-display text-4xl sm:text-5xl chrome-text uppercase leading-none">{product.name}</h3>
              {product.coming_soon ? (
                <p className="mt-2 text-[10px] tracking-[0.35em] text-zinc-600 uppercase">Price sealed until unlock</p>
              ) : (
                <p className="mt-2 font-display text-2xl chrome-text-bright">${product.price.toFixed(2)}</p>
              )}
              <p className="mt-6 text-xs leading-loose text-zinc-400">{product.description}</p>

              {product.coming_soon ? (
                <div className="mt-8">
                  <div className="border border-red-900/60 bg-red-950/20 px-5 py-4 text-center" data-testid="product-coming-soon-badge">
                    <p className="font-display text-2xl tracking-[0.15em] text-red-200 uppercase">Vault Locked</p>
                    <p className="mt-1.5 text-[10px] tracking-[0.25em] text-zinc-500 uppercase leading-relaxed">
                      Sealed until production wraps — no presales, no handouts
                    </p>
                  </div>
                  {accessReserved ? (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                      className="mt-6 border border-zinc-500/60 bg-zinc-900/40 px-5 py-7 text-center"
                      data-testid="product-access-reserved"
                    >
                      <Crown className="mx-auto h-5 w-8 text-zinc-400 mb-3" />
                      <p className="font-display text-3xl tracking-[0.12em] chrome-text-bright uppercase">Access Reserved</p>
                      <p className="mt-2.5 text-[10px] tracking-[0.25em] text-zinc-500 uppercase leading-relaxed">
                        You&apos;re on the early access list — watch your inbox when the vault opens
                      </p>
                    </motion.div>
                  ) : (
                    <>
                      <p className="mt-6 mb-3 text-[10px] tracking-[0.35em] text-zinc-500 uppercase">
                        Exclusive early access — no money down
                      </p>
                      <form onSubmit={lockIn} className="flex" data-testid="product-access-form">
                        <input
                          data-testid="product-access-input"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="YOUR EMAIL"
                          className="flex-1 border border-zinc-700 bg-zinc-950 px-4 py-3 text-xs tracking-[0.2em] text-zinc-200 placeholder:text-zinc-600 focus:border-zinc-400 focus:outline-none uppercase"
                        />
                        <button
                          data-testid="product-access-submit"
                          type="submit"
                          disabled={locking}
                          className="border border-zinc-300 bg-zinc-100 px-5 text-[10px] font-bold tracking-[0.3em] text-zinc-950 uppercase hover:bg-transparent hover:text-zinc-100 transition-colors duration-300 disabled:opacity-50"
                        >
                          {locking ? "…" : "Lock In"}
                        </button>
                      </form>
                      <p className="mt-3 text-[9px] tracking-[0.25em] text-zinc-600 uppercase leading-relaxed">
                        Just your email — you&apos;ll be first through the door
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="mt-8">
                    <p className="mb-3 text-[10px] tracking-[0.35em] text-zinc-500 uppercase">Select Size</p>
                    <div className="flex flex-wrap gap-2" data-testid="product-size-selector-live">
                      {product.sizes.map((s) => (
                        <button
                          key={s}
                          data-testid={`size-option-${s}`}
                          onClick={() => setSize(s)}
                          className={`min-w-[48px] border px-3 py-2.5 text-xs tracking-widest transition-colors duration-200 ${
                            size === s
                              ? "border-zinc-200 bg-zinc-100 text-zinc-950 font-bold"
                              : "border-zinc-700 text-zinc-400 hover:border-zinc-400 hover:text-zinc-100"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    data-testid="product-add-to-cart"
                    onClick={handleAdd}
                    className="mt-10 border border-zinc-400 bg-zinc-100 py-4 text-xs font-bold tracking-[0.35em] text-zinc-950 uppercase transition-colors duration-300 hover:bg-transparent hover:text-zinc-100"
                  >
                    Add to Cart — ${product.price.toFixed(0)}
                  </button>
                  <p className="mt-4 text-center text-[9px] tracking-[0.3em] text-zinc-600 uppercase">
                    Free worldwide shipping over $200
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
